// src/oura/ask/triage.ts
//
// Ask Oura (PREVIEW), use case 1: the morning triage. "What needs me today?"
// across every client, in four groups ordered by urgency:
//   Late on Delivery   every deliverable Delivery counts late, and every fix
//                      that came back: the same rule as Delivery's header
//                      ("6 deliverables late") and its Came back tag, so the
//                      triage and Delivery give one number
//   Waiting on us      a client replied in a doc and waits on our answer
//                      (Documents (all clients), Needs us), or sent a
//                      request we have not marked Done (the Client tab's
//                      Requests, Needs us)
//   Ready for a yes    answers the client approved in an Answer Plan that
//                      no page draft has started from (agreed.ts)
//   Gone quiet         a client who has answered none of the documents we
//                      sent them
// Only clients with records in Oura are read; a client with none adds no
// row. Every row opens the exact drawer or screen it is about.

import type { AcrossRow } from "@service/aeo/clientDocsTypes";
import type { ChangeRequest } from "@service/aeo/clientReview";
import type { Deliverable } from "@service/aeo/delivery";
import { askedText, requestIsOurs } from "../components/client/changeRequests";
import { dayMonth } from "../visibility/format";
import type { AgreedSet } from "./agreed";
import { planPageHref } from "./planHref";
import { contactOf, hrefs, n, wd } from "./text";
import type { AllData } from "./useAskData";
import type { TriageGroup, TriageRow } from "./types";

const time = (iso: string | null | undefined) => (iso ? Date.parse(iso) || 0 : 0);

/** A client's words, cut to a readable line. */
function quote(text: string, max = 96): string {
    const t = text.replace(/\s+/g, " ").trim();
    return `“${t.length > max ? `${t.slice(0, max - 1).replace(/[\s,.;:]+\S*$/, "")}…` : t}”`;
}

/** Why a late deliverable is late, in the words its Delivery row uses. */
function lateWhy(x: Deliverable): string {
    if (x.actor?.cameBack) return x.next?.text ? `${x.next.text}.` : "The latest scan found the problem again.";
    if (x.group === "waiting_client") return `${x.items.find((i) => i.waitingOn === "client" && i.clientAsk)?.clientAsk ?? x.clientLine}`;
    if (x.group === "checking") return x.clientLine;
    return x.next?.text ? `${x.next.text}.` : x.clientLine;
}

/** Delivery's own late rule: the header counts `actor.late`, the tag counts came back. */
export const isLate = (x: Deliverable) => Boolean(x.actor?.late);
export const cameBack = (x: Deliverable) => Boolean(x.actor?.cameBack);

/** Came back first, then ours, then the client's, then Oura's re-check. */
const LATE_ORDER = (x: Deliverable) => (cameBack(x) ? 0 : x.group === "needs_us" ? 1 : x.group === "waiting_client" ? 2 : 3);

function lateRow(businessId: string, client: string, contact: string, x: Deliverable): TriageRow {
    const when = x.actor?.when ?? "";
    const head = `${x.code} ${x.label}`;
    const text = cameBack(x)
        ? `${head} came back. ${lateWhy(x)}`
        : x.group === "waiting_client"
          ? `${head} is late, waiting on ${contact}. ${lateWhy(x)}`
          : x.group === "checking"
            ? `${head} is late, and Oura re-checks it ${when || "soon"}. ${lateWhy(x)}`
            : `${head} is late. ${lateWhy(x)}`;
    const age = cameBack(x) ? "Came back" : x.group === "waiting_client" ? `Waiting ${when}` : x.group === "checking" ? `Oura re-checks ${when || "soon"}` : `Due ${when}`;
    return {
        id: `late:${businessId}:${x.key}`,
        businessId,
        client,
        text,
        age,
        late: true,
        go: { label: `Open ${x.code}`, to: hrefs.deliverable(businessId, x.key) },
        since: LATE_ORDER(x),
    };
}

function docRow(r: AcrossRow): TriageRow {
    const who = r.lastClientPost?.author.name ?? "The client";
    const title = r.title.length > 48 ? `${r.title.slice(0, 47).replace(/[\s,]+\S*$/, "")}…` : r.title;
    return {
        id: `doc:${r.businessId}:${r.documentId}`,
        businessId: r.businessId,
        client: r.businessName,
        text: r.lastClientPost ? `${who} replied in the ${title}: ${quote(r.lastClientPost.text)}` : `The ${title} waits on our answer.`,
        age: wd(r.waitingWorkingDays) ?? "Waiting",
        late: Boolean(r.lateWorkingDays),
        go: { label: "Open the doc", to: hrefs.doc(r.businessId, r.documentId) },
        since: time(r.waitingSince),
    };
}

function requestRow(businessId: string, client: string, r: ChangeRequest): TriageRow {
    return {
        id: `request:${businessId}:${r.id}`,
        businessId,
        client,
        text: `${r.requestedBy} asked${r.reply ? ", and we replied, but it is not done" : ""}: ${quote(r.note)}`,
        age: askedText(r.at) ?? "Asked",
        go: { label: "Open the request", to: hrefs.request(businessId, r.id) },
        since: time(r.at),
    };
}

function yesRow(businessId: string, client: string, a: AgreedSet): TriageRow {
    const k = a.answers.length;
    const days = Array.from(new Set(a.answers.map((x) => dayMonth(x.at))));
    return {
        id: `yes:${businessId}:${a.docId}`,
        businessId,
        client,
        text: `${a.clientName} approved ${n(k, "answer")} in the ${a.docTitle}. ${k === 1 ? "It can" : "They can"} become page changes.`,
        age: `Approved ${days.join(" and ")}`,
        go: { label: "Draft them in Page workshop", to: planPageHref(businessId) },
        since: Math.min(...a.answers.map((x) => time(x.at))),
    };
}

/**
 * Gone quiet, the one definition both "What needs me today?" and "Who has gone
 * quiet?" use: a linked folder, at least one document sent, and none answered.
 * A client we have sent nothing to has not gone quiet.
 */
export const isGoneQuiet = (c: { folderLinked: boolean; replyBasis: { sent: number; answered: number } }): boolean =>
    c.folderLinked && c.replyBasis.sent > 0 && c.replyBasis.answered === 0;

/** The four groups, every row in them, before any filter. */
export function triageGroups(a: AllData, only?: string | null): TriageGroup[] {
    const keep = (id: string) => !only || id === only;
    const names = new Map((a.across?.clients ?? []).map((c) => [c.businessId, c.name] as const));
    for (const r of a.today?.rows ?? []) names.set(r.businessId, r.businessName);

    // Delivery's own deliverables, per client: the late ones and the ones that came back.
    let lateCount = 0;
    let backCount = 0;
    const late = Object.entries(a.delivery)
        .filter(([id]) => keep(id))
        .flatMap(([id, pl]) => {
            const mine = pl.deliverables.filter((x) => isLate(x) || cameBack(x));
            lateCount += mine.filter(isLate).length;
            backCount += mine.filter((x) => !isLate(x) && cameBack(x)).length;
            const contact = contactOf({ agreed: a.agreed[id] ?? null, requests: a.requests[id] ?? null });
            return mine.map((x) => lateRow(id, names.get(id) ?? pl.businessName ?? id, contact, x));
        })
        .sort((x, y) => x.since - y.since);

    const docs = (a.across?.needsUs ?? []).filter((r) => keep(r.businessId)).map(docRow);
    const reqs = Object.entries(a.requests)
        .filter(([id]) => keep(id))
        .flatMap(([id, list]) => list.filter(requestIsOurs).map((r) => requestRow(id, names.get(id) ?? id, r)));
    const us = [...docs, ...reqs].sort((x, y) => x.since - y.since);

    const yes = Object.entries(a.agreed)
        .filter(([id]) => keep(id))
        .map(([id, set]) => yesRow(id, names.get(id) ?? id, set))
        .sort((x, y) => x.since - y.since);

    // Quiet: we sent them documents and they have answered none.
    const waits = a.across?.waitingOnClient ?? [];
    const quiet = (a.across?.clients ?? [])
        .filter((c) => isGoneQuiet(c) && keep(c.businessId))
        .map((c): TriageRow => {
            const mine = waits.filter((w) => w.businessId === c.businessId).sort((x, y) => (y.waitingWorkingDays ?? 0) - (x.waitingWorkingDays ?? 0));
            const top = mine[0];
            const lateBy = top?.lateWorkingDays ? ` The ${top.title.replace(/\s*\(.*\)$/, "")} is ${n(top.lateWorkingDays, "working day")} late.` : "";
            return {
                id: `quiet:${c.businessId}`,
                businessId: c.businessId,
                client: c.name,
                text: `No reply to the ${n(c.replyBasis.sent, "document")} we sent.${lateBy}`,
                age: top ? (wd(top.waitingWorkingDays) ?? "Waiting") : "Waiting",
                late: Boolean(top?.lateWorkingDays),
                go: top ? { label: "Open the doc", to: hrefs.doc(c.businessId, top.documentId) } : { label: "Open Client", to: hrefs.client(c.businessId) },
                since: -(top?.waitingWorkingDays ?? 0),
            };
        })
        .sort((x, y) => x.since - y.since);

    return [
        {
            key: "late",
            title: "Late on Delivery",
            hint: "Deliverables past their date, or a fix that came back",
            // Delivery's header words: "6 deliverables late", and its Came back tag.
            count: `${n(lateCount, "deliverable")}${backCount ? `, ${backCount} came back` : ""}`,
            lead: `Delivery: ${n(lateCount, "deliverable")} late${backCount ? `, ${backCount} came back` : ""}.`,
            rows: late,
        },
        { key: "us", title: "Waiting on us", hint: "The client replied or asked, and the next move is ours", rows: us },
        { key: "yes", title: "Ready for a yes", hint: "Approved by the client, ready to become page changes", rows: yes },
        { key: "quiet", title: "Gone quiet", hint: "No answer to any document we sent", rows: quiet },
    ];
}

/** "14 things across 3 clients": the total and the distinct clients behind it. */
export function triageCount(groups: TriageGroup[]): { things: number; clients: number } {
    const rows = groups.flatMap((g) => g.rows);
    return { things: rows.length, clients: new Set(rows.map((r) => r.businessId)).size };
}
